// External imports
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Internal imports
import { deleteAdditionalCost } from "api";
import { CostDetailCharge } from "api/models";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { formatPrice } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";

interface ConfirmRemoveCostDialogProps {
  isOpen: boolean;
  close: () => void;
  selectedCostChild: CostDetailCharge;
}

const ConfirmRemoveCostDialog = function ({ isOpen, close, selectedCostChild }: ConfirmRemoveCostDialogProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { id: costId, amount_target, target_currency_code, description } = selectedCostChild;
  const shipmentId = useParams().id!.toUpperCase();

  const { mutate: deleteCostMutation, isLoading } = useMutation({
    mutationFn: () => deleteAdditionalCost(costId),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "tracking.components.removeCostDialog.deleteSuccess",
          defaultMessage: "Additional cost deleted",
        }),
        { type: "success" },
      );
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(shipmentId) });
      close();
    },
    onError: () =>
      errorToast(
        intl.formatMessage({
          id: "tracking.components.removeCostDialog.deleteFail",
          defaultMessage: "Fail to delete additional cost",
        }),
      ),
  });

  return (
    <Dialog
      show={isOpen}
      title={intl.formatMessage({
        id: "tracking.components.removeCostDialog.confirmDelete",
        defaultMessage: "Confirm delete",
      })}
      onHide={close}
      size="sm"
    >
      <h6 className="mb-4">
        <FormattedMessage
          id="tracking.components.removeCostDialog.confirmDeleteMessage"
          defaultMessage="Are you sure you want to delete this additional cost/discount?"
        />
      </h6>

      <Row>
        <Col xs={3} className="text-secondary">
          <FormattedMessage id="general.description" defaultMessage="Description" />
        </Col>
        <Col>{description}</Col>
      </Row>
      <Row>
        <Col xs={3} className="text-secondary">
          <FormattedMessage id="general.amount" defaultMessage="Amount" />
        </Col>
        <Col>{formatPrice(amount_target, target_currency_code)}</Col>
      </Row>

      <div className="d-flex" style={{ marginTop: 16 }}>
        <Button
          disabled={isLoading}
          className="rounded-pill font-b1"
          variant="outline-primary"
          style={{ padding: "8px 24px" }}
          label={intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
          onClick={close}
        />
        <Button
          disabled={isLoading}
          className="ml-auto rounded-pill font-b1"
          variant="danger"
          style={{ padding: "8px 24px" }}
          label={intl.formatMessage({
            id: "general.delete",
            defaultMessage: "Delete",
          })}
          onClick={() => deleteCostMutation()}
        />
      </div>
    </Dialog>
  );
};

ConfirmRemoveCostDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

export default ConfirmRemoveCostDialog;
